/**
 * cleanup-helpers/callable-reports.ts
 *
 * Wrappers around Cloud Functions that read data and produce diagnostic
 * reports without mutating state. Each function logs a formatted report to
 * the console for operators.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import type { HealthCheckResult } from './types';

/**
 * Debug Categories - Check full category structure by l1/l2 (Issue #92)
 */
export async function debugCategoryStructure(): Promise<{
  totalCategories: number;
  structure: Record<string, Record<string, Array<{ id: string; type: string; l1: string; l2: string }>>>;
  patternCheck: Record<string, { exists: boolean; categoryId?: string }>;
}> {
  console.log('[debugCategories] Fetching category structure...');

  const debugFn = httpsCallable<
    Record<string, never>,
    {
      totalCategories: number;
      structure: Record<string, Record<string, Array<{ id: string; type: string; l1: string; l2: string }>>>;
      patternCheck: Record<string, { exists: boolean; categoryId?: string }>;
    }
  >(functions, 'debugCategories');

  try {
    const result = await debugFn({});
    const data = result.data;

    console.log('📊 Category Structure Results');
    console.log('============================');
    console.log(`\n총 카테고리: ${data.totalCategories}개\n`);

    console.log('=== 카테고리 구조 (지출) ===\n');
    Object.keys(data.structure).sort().forEach(l1 => {
      console.log(`📁 ${l1}`);
      Object.keys(data.structure[l1]).sort().forEach(l2 => {
        const categories = data.structure[l1][l2];
        console.log(`  ├─ ${l2} (${categories.length}개)`);
        categories.forEach(cat => {
          console.log(`     └─ ID: ${cat.id}`);
        });
      });
    });

    console.log('\n\n=== 패턴 매칭 체크 ===\n');
    Object.entries(data.patternCheck).forEach(([pattern, info]) => {
      console.log(`${pattern}: ${info.exists ? '✓ 존재 (ID: ' + info.categoryId + ')' : '✗ 없음'}`);
    });

    console.log('\n✅ Category structure loaded');

    return data;
  } catch (error) {
    console.error('[debugCategories] Error:', error);
    throw error;
  }
}

/**
 * Health Check - Comprehensive diagnostic tool (Issue #89)
 * Diagnoses all classification system issues in one command
 */
export async function healthCheck(): Promise<HealthCheckResult> {
  console.log('[healthCheck] Running comprehensive health check...');

  const healthCheckFn = httpsCallable<Record<string, never>, HealthCheckResult>(
    functions,
    'healthCheck'
  );

  try {
    const result = await healthCheckFn({});
    const data = result.data;

    console.log('📊 Health Check Results');
    console.log('======================');
    console.log(`\n🏥 Overall Health: ${data.overallHealth.toUpperCase()}`);

    console.log('\n🔍 Merchant Field Integrity:');
    console.log(`  Valid: ${data.merchantFieldIntegrity.valid}`);
    console.log(`  Invalid: ${data.merchantFieldIntegrity.invalid}`);
    if (data.merchantFieldIntegrity.samples.length > 0) {
      console.log('  Samples:', data.merchantFieldIntegrity.samples.slice(0, 3));
    }

    console.log('\n📋 Rule Coverage:');
    console.log(`  Total Rules: ${data.ruleCoverage.totalRules}`);
    console.log(`  Total Categories: ${data.ruleCoverage.totalCategories}`);
    console.log(`  Categories with Rules: ${data.ruleCoverage.categoriesWithRules}`);
    if (data.ruleCoverage.categoriesWithoutRules.length > 0) {
      console.log(`  Categories without Rules: ${data.ruleCoverage.categoriesWithoutRules.length}`);
      console.log('  Examples:', data.ruleCoverage.categoriesWithoutRules.slice(0, 5));
    }

    console.log('\n📊 Classification Statistics:');
    console.log(`  Total Transactions: ${data.classificationStats.totalTransactions}`);
    console.log(`  Classified: ${data.classificationStats.classified} (${data.classificationStats.classificationRate}%)`);
    console.log(`  Unclassified: ${data.classificationStats.unclassified}`);
    console.log(`  Rule-Based: ${data.classificationStats.ruleBasedClassified} (${data.classificationStats.ruleMatchRate}%)`);
    console.log(`  Manual: ${data.classificationStats.manualClassified}`);

    if (data.recentErrors.length > 0) {
      console.log('\n⚠️ Recent Errors:');
      data.recentErrors.forEach((error) => {
        console.log(`  - ${error.type}: ${error.count} occurrences`);
        console.log(`    ${error.description}`);
      });
    }

    console.log('\n💡 Recommendations:');
    data.recommendations.forEach((rec: string) => {
      console.log(`  ${rec}`);
    });

    console.log('\n✅ Health check complete');

    return data;
  } catch (error) {
    console.error('[healthCheck] Error:', error);
    throw error;
  }
}

/**
 * Generate Rule Suggestions - AutoRuleGenerator (Issue #92)
 * Analyzes unclassified transactions and suggests classification rules
 */
export async function generateRuleSuggestions(minFrequency: number = 10): Promise<{
  timestamp: string;
  userId: string;
  totalUnclassified: number;
  uniqueMerchants: number;
  suggestions: Array<{
    merchant: string;
    frequency: number;
    totalAmount: number;
    avgAmount: number;
    suggestedCategory?: {
      categoryId: string;
      type: string;
      l1: string;
      l2: string;
      confidence: number;
      reason: string;
    };
    sampleTransactions: Array<{
      id: string;
      date: string;
      merchant: string;
      amount: number;
      direction: string;
    }>;
  }>;
  message: string;
}> {
  console.log(`[generateRuleSuggestions] Analyzing unclassified transactions (minFrequency: ${minFrequency})...`);

  const generateFn = httpsCallable<
    { minFrequency?: number },
    {
      timestamp: string;
      userId: string;
      totalUnclassified: number;
      uniqueMerchants: number;
      suggestions: Array<{
        merchant: string;
        frequency: number;
        totalAmount: number;
        avgAmount: number;
        suggestedCategory?: {
          categoryId: string;
          type: string;
          l1: string;
          l2: string;
          confidence: number;
          reason: string;
        };
        sampleTransactions: Array<{
          id: string;
          date: string;
          merchant: string;
          amount: number;
          direction: string;
        }>;
      }>;
      message: string;
    }
  >(functions, 'generateRuleSuggestions');

  try {
    const result = await generateFn({ minFrequency });
    const data = result.data;

    console.log('📊 Rule Suggestion Results');
    console.log('==========================');
    console.log(`\n🔢 Statistics:`);
    console.log(`  Total Unclassified: ${data.totalUnclassified}`);
    console.log(`  Unique Merchants: ${data.uniqueMerchants}`);
    console.log(`  Suggestions Generated: ${data.suggestions.length}`);

    console.log('\n📋 Top Suggestions:');
    data.suggestions.slice(0, 10).forEach((suggestion, index) => {
      console.log(`\n${index + 1}. ${suggestion.merchant}`);
      console.log(`   Frequency: ${suggestion.frequency}건`);
      console.log(`   Total Amount: ${suggestion.totalAmount.toLocaleString()}원`);
      console.log(`   Avg Amount: ${suggestion.avgAmount.toLocaleString()}원`);

      if (suggestion.suggestedCategory) {
        const cat = suggestion.suggestedCategory;
        console.log(`   ✅ Suggested: ${cat.type} > ${cat.l1} > ${cat.l2}`);
        console.log(`   Confidence: ${cat.confidence}%`);
        console.log(`   Reason: ${cat.reason}`);
        if (cat.categoryId) {
          console.log(`   Category ID: ${cat.categoryId}`);
        } else {
          console.log(`   ⚠️ Category ID: (수동 선택 필요)`);
        }
      } else {
        console.log(`   ⚠️ No category suggestion`);
      }
    });

    if (data.suggestions.length > 10) {
      console.log(`\n... and ${data.suggestions.length - 10} more suggestions`);
    }

    console.log('\n💡 Next Steps:');
    console.log('  1. Review suggestions in AutoRuleGenerator UI');
    console.log('  2. Select high-confidence suggestions');
    console.log('  3. Create rules and reclassify transactions');

    console.log(`\n✅ ${data.message}`);

    return data;
  } catch (error) {
    console.error('[generateRuleSuggestions] Error:', error);
    throw error;
  }
}
